import { useState, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { settingsApi } from '@/services/settings';
import { ApiClientError } from '@/services/api';

export interface MfaDisableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for disabling MFA
 * Requires current password for verification
 */
export default function MfaDisableModal({ isOpen, onClose, onSuccess }: MfaDisableModalProps) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!password) return;

    setIsLoading(true);
    setError(null);

    try {
      await settingsApi.disableMfa({ password });
      onSuccess();
      handleClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to disable MFA. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfa-disable-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2 id="mfa-disable-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
          Disable Two-Factor Authentication
        </h2>

        <Alert variant="warning" className="mb-4">
          Disabling two-factor authentication will make your account less secure.
        </Alert>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            To disable MFA, enter your current password to confirm.
          </p>

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="Enter your password"
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={!password}
              isLoading={isLoading}
              className="flex-1"
            >
              Disable MFA
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
